import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  IconButton,
  Typography,
  Paper,
  Link,
  InputAdornment,
  Popover,
  CircularProgress,
  Stack,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import renderAvatar from "./render-avatar";
import {Classroom, CreateNewClassroom, createClassroomPayload} from "../models/classroom";
import axios, { AxiosError } from "axios";
import { useClassroomStore } from "../store/classroom-store";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "../pages/classroom/components/rich-text/rich-text-editor";
import { School } from "@mui/icons-material";
import { useClassroomActions } from "../hooks/useClassroomActions";


function CreateClassRoomDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();

  const [classroom, setClassRoom] = useState<Classroom>(CreateNewClassroom());
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [feeInfoAnchorEl, setFeeInfoAnchorEl] = useState<HTMLElement | null>(null);
  const feeInfoOpen = Boolean(feeInfoAnchorEl);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createClassroom } = useClassroomActions();

  const handleFeeInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setFeeInfoAnchorEl(event.currentTarget);
  };

  const handleFeeInfoClose = () => {
    setFeeInfoAnchorEl(null);
  };

  const calculateStudentCharges = (): number => {
    const ins_amm = Number(classroom.instructor_amount) || 0;
    return +(Math.max(100 + ins_amm, ins_amm + (ins_amm * 0.05))).toFixed(2);
  };

  const calculatePlatformFee = (): number => {
    const ins_amm = Number(classroom.instructor_amount) || 0;
    return +(calculateStudentCharges() - ins_amm).toFixed(2);
  };
  

  const handleInputChange = (field: keyof Classroom, value: any) => {
    setClassRoom((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await createClassroom(classroom);
    setClassRoom(CreateNewClassroom());
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" >
      
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '32px 32px 24px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            backgroundSize: '200% 200%',
          },
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3, 
            position: 'relative', 
            zIndex: 1 
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <School sx={{ fontSize: 28, color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
              Create New Classroom
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 20,
            top: 20,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'scale(1.1) rotate(90deg)',
            },
            transition: 'all 0.3s ease',
            zIndex: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          backgroundColor: '#f8fafc',
          padding: '32px',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)',
          }
        }}
      >
        
        <Stack spacing={3} marginTop={2}>
          <TextField
            fullWidth
            variant="outlined"
            label="Classroom Name"
            value={classroom.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            inputProps={{ maxLength: 50 }}
            helperText={`${classroom.name.length}/50 characters`}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                fontSize: '1.1rem',
                padding: '4px 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.2)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                    borderWidth: 2,
                  },
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#667eea',
              },
            }}
          />

          {/* <RichTextEditor
            value={classroom.description}
            type="description"
            onChange={(val) => handleInputChange("description", val)}
          /> */}

          <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
            Pricing
            <Link
              component="button"
              variant="body2"
              color="primary"
              underline="hover"
              onClick={handleFeeInfoClick}
              sx={{ ml: 1, display: "flex", alignItems: "center", p: 0 }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </Link>
          </Typography>

          <Popover
            open={feeInfoOpen}
            anchorEl={feeInfoAnchorEl}
            onClose={handleFeeInfoClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { p: 2, maxWidth: 320, boxShadow: "0px 4px 12px rgba(0,0,0,0.15)" } }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              We charge a fee of ₦100 by default to mantain the integrity of classrooms, prevent spam enrollments, and ensure quality educational experiences.
            </Typography>
            <Link href="#" color="primary" variant="body2">Learn more about our fee structure</Link>
          </Popover>

        
          <Stack direction="column" spacing={2} >
            <TextField
              type="number"
              variant="outlined"
              label="Enrollment Fee"
              
              value={classroom.instructor_amount}
              onChange={(e) => handleInputChange('instructor_amount', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start" sx={{ marginLeft: 2}}>₦</InputAdornment> }}
              sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                fontSize: '1.1rem',
                padding: '4px 0',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.2)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                    borderWidth: 2,
                  },
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#667eea',
              },
            }}
            />

            <Box
              sx={{
                border: '1px solid rgb(221, 217, 217)',
                borderRadius: 1.5,
                p: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row'}} spacing={2}>
                <Box sx={{ flex: 1, textAlign: { xs: 'left', sm: 'center' } }}>
                  <Typography variant="caption" color="#6b7280" fontWeight={500}>
                    Students Pay
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="#111827">
                    ₦{calculateStudentCharges()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: { xs: 'left', sm: 'center' } }}>
                  <Typography variant="caption" color="#6b7280" fontWeight={500}>
                    Platform Fee
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="#111827">
                    ₦{calculatePlatformFee()}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: { xs: 'left', sm: 'center' } }}>
                  <Typography variant="caption" color="#6b7280" fontWeight={500}>
                    You Receive
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="#059669">
                    ₦{classroom.instructor_amount || 0}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>

          {classroom.instructor_amount > 0 && 
            <Box>
              <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 2 }}>
                Enrollment Type
              </Typography>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} >
                <ToggleButtonGroup
                  value={classroom.enrollment_type}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) handleInputChange('enrollment_type', Number(value));
                  }}
                  size="small"
                  sx={{
                    bgcolor: '#f1f5f9',
                    borderRadius: 2,
                    p: 0.5,
                    border: 'none',
                    alignSelf: { xs: 'stretch', md: 'flex-start' },
                    '& .MuiToggleButtonGroup-grouped': {
                      border: 'none',
                      borderRadius: '12px !important',
                      mx: 0.25,
                    },
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: '#64748b',
                      border: 'none',
                      flex: { xs: 1, md: 'auto' },
                      minWidth: { md: 90 },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                        color: '#3b82f6',
                      },
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#3b82f6 !important',
                      color: 'white !important',
                      boxShadow: '0 1px 3px rgba(59, 130, 246, 0.3)',
                      '&:hover': {
                        backgroundColor: '#2563eb !important',
                      },
                    },
                  }}
                >
                  <ToggleButton value={0}>One-Time</ToggleButton>
                  <ToggleButton value={1}>Recurring</ToggleButton>
                </ToggleButtonGroup>

                {classroom.enrollment_type === 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      animation: 'fadeIn 0.3s ease-out',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateX(-10px)' },
                        to: { opacity: 1, transform: 'translateX(0)' },
                      },
                    }}
                  >
                    <Typography variant="body2" color="#6b7280" fontWeight={500}>
                      Every
                    </Typography>
                    <TextField
                      type="number"
                      value={classroom.duration}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      sx={{
                        width: 80,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white',
                          borderRadius: 1.5,
                          fontSize: '0.875rem',
                          '& fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9ca3af',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: 2,
                          },
                        },
                        '& input': {
                          textAlign: 'center',
                          py: 1.25,
                        },
                      }}
                      inputProps={{ min: 1 }}
                    />
                    <Typography variant="body2" color="#6b7280" fontWeight={500}>
                      days
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          }

        </Stack>

        <Box sx={{ mt: 3 }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  By creating a new class, I have read and agreed to the{" "}
                  <Link
                    href="https://elms.ng/terms-of-use/"
                    color="primary"
                    underline="hover"
                    target="_blank"
                    rel="noopener noreferrer"
                    >
                    terms of use.
                  </Link>

                </Typography>
              }
            />
          </FormGroup>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          borderTop: "1px solid rgba(102, 126, 234, 0.1)",
        }}
      >
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!agreeToTerms || !classroom.name || (classroom.enrollment_type === 1 && classroom.duration < 1) || isSubmitting}
          sx={{
            borderRadius: 3,
            px: 6,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 700,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: '#e5e7eb',
              boxShadow: 'none',
              transform: 'none',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Creating...
            </>
          ) : (
            <>
            
              Create Classroom
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
    
  );
}

export default CreateClassRoomDialog;
